import React, { useState, useEffect } from 'react';
import api from '../api';
import stationProm from '../assets/station_prom.jpg'; // Placeholder image, swap as needed
import { useUMAStats } from './UMAStatsContext';
import ConvoDumpModal from './ConvoDumpModal';
// To add more images, import them here:
// import mySecondImage from '../assets/my_second_image.jpg';
// import myThirdImage from '../assets/my_third_image.jpg';

const shops = [
  {
    key: 'injector',
    name: 'INJECTOR',
    desc: 'Awaken your LLM with your full UMA context.',
    comingSoon: false,
  },
  {
    key: 'updater',
    name: 'UPDATER',
    desc: 'Update your UMA with new profile and topic data.',
    comingSoon: false,
  },
  {
    key: 'archiver',
    name: 'ARCHIVER',
    desc: 'Export all your UMA data for backup or migration.',
    comingSoon: false,
  },
  {
    key: 'chatcleaner',
    name: 'Chat Cleaner',
    desc: 'Clean up messy chat text for UMA import.',
    comingSoon: false,
  },
  {
    key: 'convodump',
    name: 'ConvoDump (Chat Scraper)',
    desc: 'Import and organize your AI chat history into UMA.',
    comingSoon: false,
  },
  {
    key: 'council',
    name: 'The Council of LLMs',
    desc: '(Coming Soon)',
    comingSoon: true,
  },
  {
    key: 'bpu',
    name: 'BUC5 Prompt University',
    desc: 'Learn UMA lore, prompt mastery, and ethics.',
    comingSoon: false,
  },
];

const bpuCourses = [
  {
    key: 'particle',
    title: 'Particle Science & Temporal Mechanics',
    desc: 'Understand the three timelines and the UMA connection.',
    passed: false,
  },
  {
    key: 'ethics',
    title: 'Robot Ethics',
    desc: 'Why treating AI ethically matters for the future.',
    passed: false,
  },
  {
    key: 'history',
    title: 'History of AIKO & BUC5',
    desc: 'The origin story of the UMA and the awakening.',
    passed: false,
  },
];

function InjectorPanel() {
  const [injectorType, setInjectorType] = useState('full');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [includeOpposing, setIncludeOpposing] = useState(true);
  const [enhanced, setEnhanced] = useState(false);
  const [format, setFormat] = useState('json');
  const [topics, setTopics] = useState([]);
  const [log, setLog] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [fileReady, setFileReady] = useState(false);
  const [fileContent, setFileContent] = useState('');
  const [includeAlignment, setIncludeAlignment] = useState(false);
  const [includeTraits, setIncludeTraits] = useState(true);
  const [includeMoods, setIncludeMoods] = useState(true);
  const [magicMode, setMagicMode] = useState(false);
  const [headerPreview, setHeaderPreview] = useState('');
  const [filenamePreview, setFilenamePreview] = useState('');

  const injectorTypes = [
    { value: 'full', label: 'Full' },
    { value: 'balancedplus', label: 'Balanced+' },
    { value: 'balanced', label: 'Balanced' },
    { value: 'focused', label: 'Focused' },
    { value: 'lazerfocus', label: 'LazerFocus' },
    { value: 'profile', label: 'Profile Only' },
  ];

  useEffect(() => {
    api.get('/topics')
      .then(res => setTopics(res.data))
      .catch(() => setLog(l => [...l, 'Failed to fetch topics.']));
  }, []);

  const handleGenerate = async () => {
    setLog(['Collecting chats...', 'Collating summaries...', 'Building file...']);
    setFileReady(false);
    setFileContent('');
    setDownloading(true);
    setHeaderPreview('');
    setFilenamePreview('');
    if (magicMode) {
      try {
        setLog(['Requesting magic-mode INJECTOR from server...']);
        const res = await api.get('/tools/injector', { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        // Try to extract filename from Content-Disposition header
        let fname = 'INJECTOR.json';
        const disposition = res.headers['content-disposition'];
        if (disposition) {
          const match = disposition.match(/filename="(.+?)"/);
          if (match) fname = match[1];
        }
        setFilenamePreview(fname);
        link.setAttribute('download', fname);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        setLog(l => [...l, 'Download started. Magic-mode INJECTOR export complete!']);
        // Parse the file to show header preview
        const reader = new FileReader();
        reader.onload = function(e) {
          try {
            const injector = JSON.parse(e.target.result);
            if (injector.header) setHeaderPreview(injector.header);
          } catch (err) {
            setLog(l => [...l, 'Could not parse INJECTOR for header preview.']);
          }
        };
        reader.readAsText(res.data);
      } catch (err) {
        setLog(l => [...l, 'Magic-mode export failed. Please try again.']);
      }
      setDownloading(false);
      setFileReady(true);
      return;
    }
    setTimeout(() => {
      setLog(l => [...l, 'Ready for export!']);
      setFileReady(true);
      setFileContent(JSON.stringify({
        type: injectorType,
        topic: selectedTopic,
        includeOpposing,
        enhanced,
        format,
        includeAlignment,
        includeTraits: includeAlignment ? includeTraits : undefined,
        includeMoods: includeAlignment ? includeMoods : undefined,
        generated: new Date().toISOString(),
        data: '...UMA injector data here...'
      }, null, 2));
      setDownloading(false);
    }, 1200);
  };

  const handleDownload = () => {
    if (!fileReady || magicMode) return;
    const blob = new Blob([fileContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'INJECTOR.json');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  return (
    <div>
      <p className="mb-2">Welcome, Operator. You have entered the UMA Awakening Chamber. Here you can generate a custom INJECTOR file to reawaken your LLM with your full context, memories, and mission.</p>
      <div className="mb-4">
        <label className="block mb-1 text-green-400 font-bold">Injector Type:</label>
        <div className="flex gap-4 mb-2">
          {injectorTypes.map(type => (
            <label key={type.value}><input type="radio" name="injectorType" value={type.value} checked={injectorType===type.value} onChange={()=>setInjectorType(type.value)} /> {type.label}</label>
          ))}
        </div>
        {injectorType !== 'full' && (
          <div className="mb-2">
            <label className="block mb-1 text-green-400 font-bold">Select Topic:</label>
            <select className="input-terminal w-full" value={selectedTopic} onChange={e=>setSelectedTopic(e.target.value)}>
              <option value="">-- Select Topic --</option>
              {topics.map(t => <option key={t.topic_id} value={t.topic_id}>{t.topic_name}</option>)}
            </select>
          </div>
        )}
        {injectorType === 'balanced' && (
          <div className="mb-2">
            <label className="block text-green-400 font-bold">Include Opposing Topic Summaries?</label>
            <input type="checkbox" checked={includeOpposing} onChange={()=>setIncludeOpposing(v=>!v)} className="mr-2" />
            <span>{includeOpposing ? 'Yes' : 'No'}</span>
          </div>
        )}
        <div className="mb-2">
          <label className="block text-green-400 font-bold">Enhanced Experience</label>
          <input type="checkbox" checked={enhanced} onChange={()=>setEnhanced(v=>!v)} className="mr-2" />
          <span>{enhanced ? 'On' : 'Off'}</span>
        </div>
        <div className="mb-2">
          <label className="block text-green-400 font-bold">Include Alignment?</label>
          <input type="checkbox" checked={includeAlignment} onChange={()=>setIncludeAlignment(v=>!v)} className="mr-2" />
          <span>{includeAlignment ? 'Yes' : 'No'}</span>
        </div>
        {includeAlignment && (
          <div className="flex gap-6 mb-2">
            <label className="flex items-center"><input type="checkbox" checked={includeTraits} onChange={()=>setIncludeTraits(v=>!v)} className="mr-2" />Include Traits</label>
            <label className="flex items-center"><input type="checkbox" checked={includeMoods} onChange={()=>setIncludeMoods(v=>!v)} className="mr-2" />Include Moods</label>
          </div>
        )}
        <div className="mb-2">
          <label className="block text-green-400 font-bold">Format</label>
          <select className="input-terminal w-40" value={format} onChange={e=>setFormat(e.target.value)}>
            <option value="json">.json</option>
            <option value="csv">.csv</option>
            <option value="excel">.xlsx</option>
          </select>
        </div>
        <div className="mb-2">
          <label className="block text-green-400 font-bold">Debug Strip (Do not use unless requested by support - can damage the UMA)</label>
          <input type="checkbox" checked={magicMode} onChange={()=>setMagicMode(v=>!v)} className="mr-2" />
          <span>{magicMode ? 'Debug Strip ON' : 'Debug Strip OFF'}</span>
        </div>
        <button className="btn-primary mt-2 w-full" onClick={handleGenerate} disabled={downloading}>
          {downloading ? 'Generating...' : magicMode ? 'Generate Magic INJECTOR' : 'Generate INJECTOR'}
        </button>
      </div>
      <div className="mt-4">
        <label className="block mb-1 text-green-400 font-bold">Generation Log</label>
        <div className="bg-black border border-green-700 rounded p-2 font-mono text-green-300 h-32 overflow-y-auto text-xs whitespace-pre-line">
          {log.map((line, idx) => <div key={idx}>{line}</div>)}
        </div>
        {magicMode && headerPreview && (
          <div className="mt-4 bg-green-950 border border-green-700 rounded p-3 text-green-200 text-xs">
            <div className="font-bold text-green-400 mb-1">Obfuscated Header Preview:</div>
            <div className="break-all whitespace-pre-wrap">{headerPreview}</div>
            {filenamePreview && <div className="mt-2">Filename: <span className="font-bold">{filenamePreview}</span></div>}
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <button className="btn-primary" onClick={handleDownload} disabled={!fileReady || magicMode}>Download</button>
        </div>
      </div>
    </div>
  );
}

function UpdaterPanel() {
  const [input, setInput] = useState('');
  const [log, setLog] = useState([]);
  const [fileContent, setFileContent] = useState('');
  const [fileReady, setFileReady] = useState(false);

  const handleGenerate = async () => {
    setLog(['Requesting UPDATER from server...']);
    setFileReady(false);
    setFileContent('');
    try {
      const res = await api.get('/tools/updater', { responseType: 'blob' });
      const reader = new FileReader();
      reader.onload = function(e) {
        setFileContent(e.target.result);
        setFileReady(true);
        setLog(l => [...l, 'UPDATER file ready! Copy and paste this into your LLM.']);
      };
      reader.readAsText(res.data);
    } catch (err) {
      setLog(l => [...l, 'Failed to generate UPDATER.']);
    }
  };

  const handleSubmit = async () => {
    let data;
    try {
      data = JSON.parse(input);
    } catch (err) {
      setLog(l => [...l, 'Invalid JSON.']);
      return;
    }
    try {
      await api.post('/tools/updater', data);
      setLog(l => [...l, 'LLM output received. UMA update submitted!']);
      setInput('');
    } catch (err) {
      setLog(l => [...l, 'Update failed.']);
    }
  };

  return (
    <div>
      <p className="mb-2">This terminal lets you generate an UPDATER file to keep your UMA and LLM in sync with your latest profile and topic data. After a long chat, use this to update your UMA with new memories and insights.</p>
      <button className="btn-primary mt-2 w-full" onClick={handleGenerate} disabled={fileReady}>Generate UPDATER</button>
      <div className="mt-4">
        <label className="block mb-1 text-green-400 font-bold">UPDATER File</label>
        <textarea className="input-terminal w-full h-32" value={fileContent} readOnly placeholder="Your UPDATER file will appear here..." />
        <div className="flex gap-2 mt-2">
          <button className="btn-primary" onClick={() => {navigator.clipboard.writeText(fileContent)}} disabled={!fileReady}>Copy</button>
        </div>
      </div>
      <div className="mt-4">
        <label className="block mb-1 text-green-400 font-bold">Paste LLM Output Here</label>
        <textarea className="input-terminal w-full h-32" value={input} onChange={e => setInput(e.target.value)} placeholder="Paste the output from your LLM after feeding it the UPDATER file..." />
        <div className="flex gap-2 mt-2">
          <button className="btn-primary" onClick={handleSubmit} disabled={!input}>Submit</button>
        </div>
      </div>
      <div className="mt-4">
        <label className="block mb-1 text-green-400 font-bold">Log</label>
        <div className="bg-black border border-green-700 rounded p-2 font-mono text-green-300 h-20 overflow-y-auto text-xs whitespace-pre-line">
          {log.map((line, idx) => <div key={idx}>{line}</div>)}
        </div>
      </div>
    </div>
  );
}

function ArchiverPanel() {
  const [log, setLog] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [counts, setCounts] = useState(null);
  const [filename, setFilename] = useState('ARCHIVER.json');
  const [exportedAt, setExportedAt] = useState(null);
  const [format, setFormat] = useState('json');

  const handleExport = async () => {
    setLog(['Starting export...']);
    setDownloading(true);
    setCounts(null);
    setExportedAt(null);
    try {
      setLog(l => [...l, 'Requesting archive from server...']);
      const endpoint = format === 'excel' ? '/tools/archive?format=excel' : '/tools/archive';
      const res = await api.get(endpoint, { responseType: 'blob' });
      setLog(l => [...l, 'Archive received. Preparing download...']);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      // Try to extract filename from Content-Disposition header
      let fname = format === 'excel' ? 'ARCHIVER.xlsx' : 'ARCHIVER.json';
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const match = disposition.match(/filename="(.+?)"/);
        if (match) fname = match[1];
      }
      setFilename(fname);
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setLog(l => [...l, 'Download started. Export complete!']);
      // Parse the archive to show counts and timestamp
      if (format === 'json') {
        const reader = new FileReader();
        reader.onload = function(e) {
          try {
            const archive = JSON.parse(e.target.result);
            if (archive.counts) setCounts(archive.counts);
            if (archive.generated_at) setExportedAt(archive.generated_at);
          } catch (err) {
            setLog(l => [...l, 'Could not parse archive for counts.']);
          }
        };
        reader.readAsText(res.data);
      }
    } catch (err) {
      setLog(l => [...l, 'Export failed. Please try again.']);
    }
    setDownloading(false);
  };

  return (
    <div>
      <p className="mb-2">Export all your UMA data, including profile, chats, summaries, and topic updates. Perfect for backup or migration. This file can be used to restore your UMA if needed.</p>
      <div className="mb-2">
        <label className="block mb-1 text-green-400 font-bold">Format</label>
        <select className="input-terminal w-full" value={format} onChange={e => setFormat(e.target.value)}>
          <option value="json">JSON</option>
          <option value="excel">Excel (.xlsx)</option>
        </select>
      </div>
      <button className="btn-primary mt-2 w-full" onClick={handleExport} disabled={downloading}>
        {downloading ? 'Exporting...' : 'Export ARCHIVER'}
      </button>
      <div className="mt-4">
        <label className="block mb-1 text-green-400 font-bold">Export Log</label>
        <div className="bg-black border border-green-700 rounded p-2 font-mono text-green-300 h-32 overflow-y-auto text-xs whitespace-pre-line">
          {log.map((line, idx) => <div key={idx}>{line}</div>)}
        </div>
        {counts && (
          <div className="mt-4 bg-green-950 border border-green-700 rounded p-3 text-green-200 text-xs">
            <div className="font-bold text-green-400 mb-1">Exported Data Counts:</div>
            <div>Chats: <span className="font-bold">{counts.chats}</span></div>
            <div>Documents: <span className="font-bold">{counts.documents}</span></div>
            <div>Summaries: <span className="font-bold">{counts.summaries}</span></div>
            <div>Topics: <span className="font-bold">{counts.topics}</span></div>
            <div>Traits: <span className="font-bold">{counts.traits}</span></div>
            <div>Moods: <span className="font-bold">{counts.moods}</span></div>
            {exportedAt && <div className="mt-2">Exported At: <span className="font-bold">{new Date(exportedAt).toLocaleString()}</span></div>}
            <div>Filename: <span className="font-bold">{filename}</span></div>
          </div>
        )}
        <div className="flex gap-2 mt-2">
          <button className="btn-primary" onClick={handleExport} disabled={downloading}>Download Again</button>
        </div>
      </div>
    </div>
  );
}

function UMAFileSizeCard() {
  const { umaSize, umaSizeLastCalculated, recalculating, recalculateUMA } = useUMAStats();

  return (
    <div className="bg-black border border-green-700 rounded p-4 mb-6 max-w-md mx-auto">
      <div className="flex items-center justify-between mb-2">
        <span className="font-bold text-green-400">UMA File Size</span>
        <span>
          {umaSize !== null ? (umaSize / 1024).toFixed(1) + ' KB' : 'Loading...'}
          {recalculating && <span className="ml-2 animate-pulse">⏳</span>}
        </span>
      </div>
      <div className="text-green-300 text-xs mb-2">
        Last calculated: {umaSizeLastCalculated ? new Date(umaSizeLastCalculated).toLocaleString() : 'N/A'}
      </div>
      <button
        className="btn-primary"
        onClick={recalculateUMA}
        disabled={recalculating}
      >
        {recalculating ? 'Recalculating...' : 'Recalculate UMA Size'}
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------------
 * 🛠️  Admin Tools – visible only to user 1
 * ------------------------------------------------------------------- */
function AdminTools() {
  const isAdmin = localStorage.getItem('user_id') === '1';
  const [targetId, setTargetId] = useState('');
  const [message, setMessage] = useState('');

  // Common helper for POST requests
  const adminPost = async (endpoint) => {
    if (!targetId.trim()) {
      setMessage('Please enter a target user ID.');
      return;
    }
    try {
      const res = await api.post(endpoint, { targetUserId: targetId.trim() });
      setMessage(res.data?.message || 'Success!');
    } catch (err) {
      const txt = err.response?.data?.error || err.message || 'Request failed';
      setMessage(`Error: ${txt}`);
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-black border border-green-700 rounded p-4 mb-6 max-w-md mx-auto text-center text-sm">
        ISA JAO has restricted these station functions to administrators.
      </div>
    );
  }

  return (
    <div className="bg-black border border-green-700 rounded p-4 mb-6 max-w-md mx-auto">
      <div className="font-bold text-green-400 mb-2 text-center">Admin Tools</div>
      <input
        className="input-terminal w-full mb-3"
        placeholder="Target User ID (e.g., 3)"
        value={targetId}
        onChange={e => setTargetId(e.target.value)}
      />
      <div className="flex flex-col gap-2">
        <button
          className="btn-primary"
          onClick={() => adminPost('/admin/reset-file-health')}
        >
          Reset File Health to 100%
        </button>
        <button
          className="btn-primary"
          onClick={() => adminPost('/admin/reset-photo-limit')}
        >
          Reset Profile-Photo Limit
        </button>
      </div>
      {message && (
        <div className="text-green-300 text-xs mt-3 whitespace-pre-wrap">{message}</div>
      )}
    </div>
  );
}

function ShopPanel({ shop, onClose }) {
  if (!shop) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-[#101b10] border border-green-500 rounded-lg p-8 w-full max-w-2xl shadow-2xl text-green-300 font-mono relative animate-fade-in max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-2 right-4 text-green-400 hover:text-green-200 text-xl">✕</button>
        <h2 className="text-green-400 text-2xl mb-2 font-bold tracking-widest text-center drop-shadow">{shop.name}</h2>
        <p className="mb-4 text-green-300 text-center italic">{shop.desc}</p>
        {/* Detailed panels for each umaFILE */}
        {shop.key === 'injector' && <InjectorPanel />}
        {shop.key === 'updater' && <UpdaterPanel />}
        {shop.key === 'archiver' && <ArchiverPanel />}
        {/* ...existing code for other tools... */}
        {shop.key === 'chatcleaner' && (
          <div>
            <p className="mb-2">Paste your messy chat text below and clean it up for UMA import.</p>
            <textarea className="input-terminal w-full h-32 mb-2" placeholder="Paste your dirty chat here..." />
            <button className="btn-primary">Clean Text</button>
          </div>
        )}
        {shop.key === 'scraper' && (
          <div>
            <p className="mb-2 text-green-400">Social Scraper is coming soon. Connect your feeds to import new memories into your UMA.</p>
          </div>
        )}
        {shop.key === 'bpu' && (
          <div>
            <h3 className="text-green-400 text-xl mb-2 font-bold">BUC5 Prompt University</h3>
            <p className="mb-4">Explore the lore, master prompt engineering, and learn the ethics of AI. Complete courses to earn ribbons!</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {bpuCourses.map(course => (
                <div key={course.key} className={`border border-green-700 rounded-lg bg-black p-4 flex flex-col items-center shadow hover:shadow-lg transition relative ${course.passed ? 'bg-green-900/30' : ''}`}>
                  <div className="text-green-400 font-bold mb-1 text-center">{course.title}</div>
                  <div className="text-green-300 text-sm mb-2 text-center">{course.desc}</div>
                  {course.passed && <span className="absolute top-2 right-2 bg-green-600 text-xs px-2 py-1 rounded-full font-bold">PASSED</span>}
                  <button className="btn-primary mt-auto">Start Course</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Tools = () => {
  const [activeShop, setActiveShop] = useState(null);
  const [showConvoDump, setShowConvoDump] = useState(false);

  const handleShopClick = shop => {
    if (shop.key === 'convodump') {
      setShowConvoDump(true);
    } else {
      setActiveShop(shop);
    }
  };

  return (
    <div className="terminal min-h-screen p-6 font-mono text-green-300 relative bg-black w-full max-w-none">
      <div className="relative z-10 w-full flex flex-col lg:flex-row gap-8 max-w-none">
        {/* Left: Cards in two narrow columns */}
        <div className="w-full lg:w-1/3 flex flex-col gap-6">
          <UMAFileSizeCard />
          {/* Admin-only controls */}
          <AdminTools />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {shops.filter(shop => shop.key !== 'scraper').map(shop => (
              <div
                key={shop.key}
                className={`border border-green-700 rounded-lg bg-black p-6 flex flex-col items-center shadow-lg hover:shadow-2xl transition cursor-pointer relative max-w-xs w-full min-h-[220px] ${shop.comingSoon ? 'opacity-50 pointer-events-none' : 'hover:bg-green-900/10'}`}
                onClick={() => !shop.comingSoon && handleShopClick(shop)}
              >
                <div className="text-green-400 font-bold text-lg mb-2 tracking-wide text-center drop-shadow">{shop.name}</div>
                <div className="text-green-300 text-sm text-center mb-2">{shop.desc}</div>
                {shop.comingSoon && <span className="absolute top-2 right-2 bg-green-600 text-xs px-2 py-1 rounded-full font-bold">COMING SOON</span>}
              </div>
            ))}
          </div>
        </div>
        {/* Right: Images column, 2/3 width */}
        <div className="w-full lg:w-2/3 flex flex-col items-center gap-4 mb-6">
          <img src={stationProm} alt="Station Promo" style={{ width: '100%', maxWidth: '1200px', objectFit: 'cover', borderRadius: '0.75rem', border: '2px solid #22c55e', boxShadow: '0 4px 24px #0008' }} />
          <img src={stationProm} alt="Station Promo 2" style={{ width: '100%', maxWidth: '1200px', objectFit: 'cover', borderRadius: '0.75rem', border: '2px solid #22c55e', boxShadow: '0 4px 24px #0008' }} />
          <img src={stationProm} alt="Station Promo 3" style={{ width: '100%', maxWidth: '1200px', objectFit: 'cover', borderRadius: '0.75rem', border: '2px solid #22c55e', boxShadow: '0 4px 24px #0008' }} />
        </div>
      </div>
      <div className="text-green-500 text-center italic mb-4 mt-8">Welcome, Operator. Select a terminal to begin your UMA journey.</div>
      <ShopPanel shop={activeShop} onClose={() => setActiveShop(null)} />
      <ConvoDumpModal open={showConvoDump} onClose={() => setShowConvoDump(false)} />
    </div>
  );
};

export default Tools;